import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * ThemeContext — single source of truth for light/dark mode.
 *
 * The actual colour values live in src/styles/theme.css under
 *   :root { ... }                      (light)
 *   [data-theme="dark"] { ... }        (dark)
 *
 * This provider only flips the `data-theme` attribute on <html>
 * and persists the user's choice to localStorage.
 *
 * Initial value is decided in three layers (highest wins):
 *   1. explicit user choice in localStorage ("tekhub.theme")
 *   2. OS preference (prefers-color-scheme)
 *   3. fallback to "light"
 *
 * The flash-of-unstyled-content prevention runs as an inline script in
 * public/index.html *before* React hydrates, so the right theme is
 * already painted by the time this provider mounts.
 */

const STORAGE_KEY = 'tekhub.theme';
const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

function readInitialTheme() {
  if (typeof document === 'undefined') return 'light';
  // The inline script in index.html has already set data-theme.
  // Read it back so React state is consistent with the DOM.
  const fromDom = document.documentElement.getAttribute('data-theme');
  if (fromDom === 'light' || fromDom === 'dark') return fromDom;
  // Fallbacks if the inline script didn't run for some reason
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) { /* localStorage may be blocked */ }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  // Apply theme to <html> + persist
  const applyTheme = useCallback((next) => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    // Pause transitions during the flip so the entire UI repaints together
    html.classList.add('tk-theme-flipping');
    html.setAttribute('data-theme', next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* ignore */ }
    // Re-enable transitions on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => html.classList.remove('tk-theme-flipping'));
    });
  }, []);

  useEffect(() => { applyTheme(theme); }, [theme, applyTheme]);

  // Follow OS changes when the user hasn't expressed a preference
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved) return; // user has a stored preference, don't override
      } catch (err) { /* ignore */ }
      setThemeState(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
